import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Empty, Pill } from "../components/Bits";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CloudArrowUpIcon, PlusIcon, MagnifyingGlassIcon, TrashIcon } from "@phosphor-icons/react";
import { formatDate } from "../lib/format";

export default function Prospects() {
    const [lists, setLists] = useState([]);
    const [activeListId, setActiveListId] = useState(null);
    const [prospects, setProspects] = useState([]);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [newList, setNewList] = useState({ name: "", description: "" });
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const loadLists = async () => {
        const { data } = await api.get("/prospect-lists");
        setLists(data);
        if (data.length && !activeListId) setActiveListId(data[0].id);
        if (data.length === 0) setActiveListId(null);
    };

    const loadProspects = async () => {
        if (!activeListId) { setProspects([]); return; }
        const { data } = await api.get("/prospects", { params: { list_id: activeListId, search: search || undefined, limit: 500 } });
        setProspects(data);
    };

    useEffect(() => { loadLists(); }, []);
    useEffect(() => { loadProspects(); /* eslint-disable-next-line */ }, [activeListId, search]);

    const createList = async () => {
        if (!newList.name.trim()) return;
        const { data } = await api.post("/prospect-lists", newList);
        setLists((prev) => [data, ...prev]);
        setActiveListId(data.id);
        setShowCreate(false);
        setNewList({ name: "", description: "" });
        toast.success("List created");
    };

    const handleFile = async (file) => {
        if (!activeListId) {
            toast.error("Create or select a list first");
            return;
        }
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(`/prospect-lists/${activeListId}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported ${data.inserted} prospects (${data.invalid} skipped)`);
            await loadLists();
            await loadProspects();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) handleFile(f);
    };

    const removeList = async (id) => {
        if (!window.confirm("Delete this list and all its prospects?")) return;
        await api.delete(`/prospect-lists/${id}`);
        toast.success("List deleted");
        if (activeListId === id) setActiveListId(null);
        await loadLists();
    };

    const activeList = lists.find((l) => l.id === activeListId);

    return (
        <div className="pb-12">
            <PageHeader
                overline="DATABASE"
                title="Prospects"
                description="Upload, segment, and manage the prospects your AI agents will dial."
                testid="prospects-header"
                actions={
                    <>
                        <button
                            type="button"
                            onClick={() => setShowCreate(true)}
                            className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2"
                            data-testid="create-list-button"
                        >
                            <PlusIcon size={14} weight="bold" /> New list
                        </button>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="btn-primary px-4 py-2.5 text-[13px] flex items-center gap-2"
                            data-testid="upload-csv-trigger"
                            disabled={!activeListId}
                        >
                            <CloudArrowUpIcon size={14} weight="bold" /> Upload CSV
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="csv-file-input"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                        />
                    </>
                }
            />

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-4 gap-3">
                {/* Lists sidebar */}
                <div className="swiss-section p-4 lg:col-span-1" data-testid="lists-panel">
                    <div className="overline mb-3">PROSPECT LISTS</div>
                    {lists.length === 0 ? (
                        <p className="text-[12px] text-[var(--text-secondary)]">No lists yet.</p>
                    ) : (
                        <ul className="space-y-1" data-testid="lists-list">
                            {lists.map((l) => (
                                <li key={l.id}>
                                    <button
                                        type="button"
                                        onClick={() => setActiveListId(l.id)}
                                        data-testid={`list-item-${l.id}`}
                                        className={`w-full text-left px-3 py-2.5 text-[13px] flex items-center justify-between gap-2 transition-colors ${
                                            activeListId === l.id
                                                ? "bg-[var(--primary)] text-white"
                                                : "hover:bg-[var(--surface-active)]"
                                        }`}
                                    >
                                        <span className="truncate">{l.name}</span>
                                        <span className="mono text-[11px] opacity-70">{l.record_count}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Main */}
                <div className="lg:col-span-3 space-y-3">
                    {/* Drop zone */}
                    <div
                        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={`swiss-section p-6 border-2 border-dashed transition-all ${dragOver ? "border-[var(--primary)] bg-[var(--surface-active)]" : "border-[var(--border)]"}`}
                        data-testid="dropzone"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[var(--primary)] flex items-center justify-center" aria-hidden="true">
                                <CloudArrowUpIcon size={18} weight="bold" className="text-white" />
                            </div>
                            <div className="flex-1">
                                <div className="font-semibold text-[14px]">Drag & drop a CSV here, or click upload</div>
                                <div className="text-[12px] text-[var(--text-secondary)] font-mono mt-0.5">
                                    Required columns: name, phone · Optional: email, company, title, industry, company_size, notes
                                </div>
                            </div>
                            {uploading && <span className="overline text-[var(--accent)]">UPLOADING…</span>}
                        </div>
                    </div>

                    {!activeList ? (
                        <Empty
                            title="Create a list to get started"
                            description="Lists segment prospects so you can run distinct campaigns. Use 'New list' above."
                            testid="empty-no-list"
                        />
                    ) : (
                        <>
                            <div className="swiss-section p-5" data-testid="active-list-meta">
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <div>
                                        <div className="overline">ACTIVE LIST</div>
                                        <h3 className="text-2xl font-bold tracking-tight mt-1">{activeList.name}</h3>
                                        {activeList.description && (
                                            <p className="text-[13px] text-[var(--text-secondary)] mt-1">{activeList.description}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <Stat k="RECORDS" v={activeList.record_count} />
                                        <Stat k="VALID" v={activeList.valid_count} accent />
                                        <Stat k="INVALID" v={activeList.invalid_count} />
                                        <button
                                            type="button"
                                            onClick={() => removeList(activeList.id)}
                                            className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-1.5"
                                            data-testid={`delete-list-${activeList.id}`}
                                        >
                                            <TrashIcon size={12} weight="bold" /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="swiss-section">
                                <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-3">
                                    <MagnifyingGlassIcon size={14} weight="bold" className="text-[var(--text-secondary)]" />
                                    <input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name, company or phone…"
                                        className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[var(--text-secondary)]"
                                        data-testid="prospects-search"
                                    />
                                </div>
                                {prospects.length === 0 ? (
                                    <div className="p-10 text-center text-[var(--text-secondary)] text-[13px]">No prospects found.</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-[13px]" data-testid="prospects-table">
                                            <thead className="text-left">
                                                <tr className="border-b border-[var(--border)] [&>th]:py-2.5 [&>th]:px-4 [&>th]:overline [&>th]:text-[10px]">
                                                    <th>Name</th>
                                                    <th>Phone</th>
                                                    <th>Company</th>
                                                    <th>Title</th>
                                                    <th>Status</th>
                                                    <th>Calls</th>
                                                    <th>Last Called</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {prospects.map((p) => (
                                                    <tr key={p.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-active)]" data-testid={`prospect-row-${p.id}`}>
                                                        <td className="py-2.5 px-4 font-semibold">{p.name}</td>
                                                        <td className="py-2.5 px-4 mono">{p.phone}</td>
                                                        <td className="py-2.5 px-4">{p.company || "—"}</td>
                                                        <td className="py-2.5 px-4 text-[var(--text-secondary)]">{p.title || "—"}</td>
                                                        <td className="py-2.5 px-4"><StatusPill status={p.status} /></td>
                                                        <td className="py-2.5 px-4 mono">{p.call_count}</td>
                                                        <td className="py-2.5 px-4 mono text-[var(--text-secondary)]">{p.last_called_at ? formatDate(p.last_called_at) : "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="rounded-[2px]" data-testid="create-list-dialog">
                    <DialogHeader>
                        <DialogTitle>Create prospect list</DialogTitle>
                        <DialogDescription>Lists segment prospects so you can run distinct campaigns against each one.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline mb-1.5 block">Name</label>
                            <input
                                value={newList.name}
                                onChange={(e) => setNewList((s) => ({ ...s, name: e.target.value }))}
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                placeholder="Q2 Outbound — Mid-Market"
                                data-testid="new-list-name"
                            />
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Description</label>
                            <input
                                value={newList.description}
                                onChange={(e) => setNewList((s) => ({ ...s, description: e.target.value }))}
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                placeholder="Optional context"
                                data-testid="new-list-desc"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost px-4 py-2 text-[13px]" data-testid="new-list-cancel">Cancel</button>
                        <button type="button" onClick={createList} className="btn-primary px-4 py-2 text-[13px]" data-testid="new-list-submit">Create list</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Stat = ({ k, v, accent }) => (
    <div>
        <div className="overline mb-1">{k}</div>
        <div className={`mono text-2xl font-semibold ${accent ? "text-[var(--accent)]" : ""}`}>{v}</div>
    </div>
);

const STATUS_COLORS = {
    new: "#4B5563",
    contacted: "#0033A0",
    qualified: "#059669",
    appointment_set: "#059669",
    not_interested: "#6B7280",
    dnc: "#E02424",
};
const STATUS_LABELS = {
    new: "New",
    contacted: "Contacted",
    qualified: "Qualified",
    appointment_set: "Booked",
    not_interested: "Not Int.",
    dnc: "DNC",
};
const StatusPill = ({ status }) => (
    <Pill color={STATUS_COLORS[status] || "#111827"}>{STATUS_LABELS[status] || status}</Pill>
);
